import Alert from 'components/Alert';
import Snackbar from '@mui/material/Snackbar';
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetter,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import React, { useEffect, useMemo, useState } from 'react';
import PageContentLayout from 'layouts/PageContentLayout';
import PageLayout from 'layouts/PageLayout';
import GridWithStateWrapper from 'components/GridWidthStateWrapper';
import moment from 'moment';
import OnlyTitleSummaryBar from 'components/SummaryBars/OnlyTitleSummaryBar';
import { UserTraining } from 'common/models/UserTraining';
import { GetNextSessions } from 'services/UserTrainingService';
const routerLinkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'underline' };

export default function UserTrainingPage() {
  const [summaryBar, setSummaryBar] = useState<JSX.Element>(<></>);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [rows, setRows] = useState<UserTraining[]>([]);
  const apiRef = useGridApiRef();

  useEffect(() => {
    const summaryBar = (
      <OnlyTitleSummaryBar title="Training Sessions" browserTabTitle="Training Sessions" />
    );
    setSummaryBar && setSummaryBar(summaryBar);
  }, []);

  useEffect(() => {
    const getRows = async () => {
      setIsLoading(true);
      const rows = await GetNextSessions(setErrorMessage);
      if (rows) setRows(rows);
      setIsLoading(false);
    };
    getRows();
  }, []);

  const columns = useMemo<GridColDef[]>(() => {
    const dateValueGetter: GridValueGetter<UserTraining, any, undefined, string> = (value) => {
      if (value) {
        const d = moment(value);
        if (d.isValid() && d.get('year') > 1) {
          return d.toDate();
        }
      }
      return null;
    };

    const urlRenderer = (params: GridRenderCellParams) => (
      <a href={params.value} target="_blank" rel="noopener noreferrer" style={routerLinkStyle}>
        Click to view Session
      </a>
    );
    const dateRenderer = (params: GridRenderCellParams) => {
      if (params.value) {
        return moment(params.value).format('DD MMM YYYY hh:mm A');
      }
    };

    return [
      {
        field: 'trainingDate',
        headerName: 'Date',
        type: 'dateTime',
        width: 200,
        valueGetter: dateValueGetter,
        renderCell: dateRenderer,
      },
      {
        field: 'trainingType',
        headerName: 'Training Type',
        width: 300,
      },
      { field: 'url', headerName: 'Url', width: 500, renderCell: urlRenderer },
    ];
  }, []);

  return (
    <PageLayout SummaryBar={summaryBar}>
      <Snackbar open={showSuccess} autoHideDuration={3000} onClose={() => setShowSuccess(false)}>
        <Alert onClose={() => setShowSuccess(false)}>Changes Saved</Alert>
      </Snackbar>
      <Snackbar open={errorMessage !== ''} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <PageContentLayout showLoading={isLoading}>
        <GridWithStateWrapper
          gridName="user-training"
          getRowId={(row) => row.id}
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          pagination
          disableColumnFilter
          disableColumnMenu
          hideHeaderFilters
        />
      </PageContentLayout>
    </PageLayout>
  );
}
